'use client';

import { consoleLog, isLocalDevelopment, maybeReloadOnChunkError } from '@/utils';
import { ErrorState } from '@/components/ErrorState';
import { FEATURE_FLAGS } from '@/constants';
import { getApplicationInsights } from '@/lib/application-insights';
import { useEffect } from 'react';

/**
 * Global error boundary — the application's last line of defense.
 *
 * Unlike `app/error.tsx`, this boundary catches errors thrown in the root
 * layout/template themselves, and therefore REPLACES the root layout. For that
 * reason it must render its own `<html>` and `<body>` elements.
 *
 * Telemetry note:
 * - Because it replaces the layout, the `<AppInsights />` component is not
 *   mounted here, so we report by calling the Application Insights singleton
 *   directly via `getApplicationInsights()`.
 *
 * Runtime note:
 * - This component only renders in production builds; in development Next.js
 *   shows its error overlay instead.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Post-deploy chunk-load failures are recoverable: reload once onto the
    // current build before falling back to the manual error UI below.
    if (maybeReloadOnChunkError(error)) return;

    consoleLog.error('Global error boundary caught an error', error);

    if (isLocalDevelopment()) return;
    if (!FEATURE_FLAGS.ENABLE_APP_INSIGHTS) return;

    getApplicationInsights()?.trackException({
      exception: error,
      properties: { digest: error.digest, boundary: 'app/global-error' },
    });
  }, [error]);

  return (
    <html lang="en">
      <body className="bg-semantic-page-bg">
        <ErrorState
          digest={error.digest}
          onRetry={reset}
          secondaryLabel="Reload page"
          onSecondary={() => {
            window.location.reload();
          }}
        />
      </body>
    </html>
  );
}
